import { z } from 'zod';
import * as dt from '../data.types.js';
import {
  BaseResponseSchema,
  integerIdSchema,
} from './primitives.js';
import { FileMetadataSchema } from './files.schemas.js';

export const FingerprintImageMetadataSchema = FileMetadataSchema.extend({
  fileId: integerIdSchema.optional(),
  blurScore: z.number().min(0).max(100).optional(),
  ocrConfidence: z.number().min(0).max(100).optional(),
  isVerifiedByUser: z.boolean().optional(),
  userMarkedAsBad: z.boolean().optional(),
  verifiedByUserId: integerIdSchema.optional(),
  verifiedAt: z.string().optional(),
  rejectionReason: z.string().optional(),
});

export const GetFingerprintImagesResponseSchema = z.object({
  images: z.array(FingerprintImageMetadataSchema),
  waferId: integerIdSchema,
  count: z.number().int().min(0),
  maxAllowed: z.number().int().min(1),
});

export const VerifyFingerprintImageRequestSchema = z.object({
  verified: z.boolean(),
});

export const MarkFingerprintImageBadRequestSchema = z.object({
  rejectionReason: z.string().min(1).max(500),
});

export const OcrResultSchema = z.object({
  text: z.string(),
  confidence: z.number().min(0).max(100),
  imageId: z.string(),
  blurScore: z.number().min(0).max(100).optional(),
});

export const ResolveOcrConflictsRequestSchema = z.object({
  ocrResults: z.array(OcrResultSchema).min(2),
  context: z.string().max(100).optional(),
});

export const ResolveOcrConflictsResponseSchema = z.object({
  finalValue: z.string(),
  finalConfidence: z.number().min(0).max(100),
  resolutionMethod: z.nativeEnum(dt.OCR_RESOLUTION_METHOD),
  requiredUserReview: z.boolean(),
  explanation: z.string(),
});

export const FingerprintImageActionResponseSchema = z.object({
  success: z.literal(true),
  message: z.string(),
});

export const ResolveOcrConflictsFlatResponseSchema = ResolveOcrConflictsResponseSchema.extend({
  success: z.literal(true),
});

export const GetFingerprintImagesApiResponseSchema = BaseResponseSchema.extend({
  data: GetFingerprintImagesResponseSchema,
});

export const VerifyFingerprintImageApiResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    success: z.boolean(),
    message: z.string(),
  }),
});

export const MarkFingerprintImageBadApiResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    success: z.boolean(),
    message: z.string(),
  }),
});

export const ResolveOcrConflictsApiResponseSchema = BaseResponseSchema.extend({
  data: ResolveOcrConflictsResponseSchema,
});
// Inferred types
export type FingerprintImageActionResponse = z.infer<typeof FingerprintImageActionResponseSchema>;
export type FingerprintImageMetadata = z.infer<typeof FingerprintImageMetadataSchema>;
export type GetFingerprintImagesApiResponse = z.infer<typeof GetFingerprintImagesApiResponseSchema>;
export type GetFingerprintImagesResponse = z.infer<typeof GetFingerprintImagesResponseSchema>;
export type MarkFingerprintImageBadApiResponse = z.infer<typeof MarkFingerprintImageBadApiResponseSchema>;
export type MarkFingerprintImageBadRequest = z.infer<typeof MarkFingerprintImageBadRequestSchema>;
export type OcrResult = z.infer<typeof OcrResultSchema>;
export type ResolveOcrConflictsApiResponse = z.infer<typeof ResolveOcrConflictsApiResponseSchema>;
export type ResolveOcrConflictsFlatResponse = z.infer<typeof ResolveOcrConflictsFlatResponseSchema>;
export type ResolveOcrConflictsRequest = z.infer<typeof ResolveOcrConflictsRequestSchema>;
export type ResolveOcrConflictsResponse = z.infer<typeof ResolveOcrConflictsResponseSchema>;
export type VerifyFingerprintImageApiResponse = z.infer<typeof VerifyFingerprintImageApiResponseSchema>;
export type VerifyFingerprintImageRequest = z.infer<typeof VerifyFingerprintImageRequestSchema>;
